/**
 * Plow Matcher — multi-step equipment matcher (metaobjects + product metafields).
 * Supports mouse + full keyboard (Tab, Enter/Space, arrow keys within option grids).
 */
(function () {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const TRANSITION_MS = 300;
  const OPTION_SEL = '[data-pm-select-type], [data-pm-select-size], [data-pm-select-style]';

  const esc = (value) =>
    String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const idEq = (a, b) => a != null && b != null && String(a) === String(b);

  const bySortOrder = (a, b) => {
    const an = Number.parseFloat(a.sortOrder);
    const bn = Number.parseFloat(b.sortOrder);
    const aOk = !Number.isNaN(an);
    const bOk = !Number.isNaN(bn);
    if (aOk && bOk && an !== bn) return an - bn;
    if (aOk !== bOk) return aOk ? -1 : 1;
    return String(a.name || a.title || '').localeCompare(String(b.name || b.title || ''));
  };

  const setVisible = (el, visible) => {
    if (!el) return;
    el.hidden = !visible;
    el.classList.toggle('d-none', !visible);
  };

  /** Mark a step container as inactive for AT + tab order. */
  const setStepInactive = (el) => {
    if (!el) return;
    el.hidden = true;
    el.setAttribute('aria-hidden', 'true');
    el.classList.remove('is-active', 'is-leaving');
    if ('inert' in el) el.inert = true;
  };

  const setStepActive = (el) => {
    if (!el) return;
    el.hidden = false;
    el.setAttribute('aria-hidden', 'false');
    el.classList.add('is-active');
    el.classList.remove('is-leaving');
    if ('inert' in el) el.inert = false;
  };

  class PlowMatcher {
    constructor(root) {
      this.root = root;
      this.storageKey = `plow-matcher:${root.id || 'default'}`;
      this.data = { machineTypes: [], machineSizes: [], plowStyles: [], matchers: [] };
      this.settings = {};
      this.state = {
        machineType: null,
        machineSize: null,
        plowStyle: null,
        matcher: null,
      };
      this.step = 1;
      this.transitioning = false;
      this.restoring = false;

      this.$ = {
        steps: {
          1: root.querySelector('[data-pm-step="1"]'),
          2: root.querySelector('[data-pm-step="2"]'),
          3: root.querySelector('[data-pm-step="3"]'),
          result: root.querySelector('[data-pm-step="result"]'),
        },
        grids: {
          types: root.querySelector('[data-pm-grid="machine-types"]'),
          sizes: root.querySelector('[data-pm-grid="machine-sizes"]'),
          styles: root.querySelector('[data-pm-grid="plow-styles"]'),
        },
        productsEmpty: root.querySelector('[data-pm-products-empty]'),
        products: root.querySelectorAll('[data-pm-product]'),
        heading2: root.querySelector('[data-pm-heading="2"]'),
        heading3: root.querySelector('[data-pm-heading="3"]'),
        status: root.querySelector('[data-pm-status]'),
        bottom: root.querySelector('[data-pm-bottom]'),
        bottomHeading: root.querySelector('[data-pm-bottom-heading]'),
        bottomText: root.querySelector('[data-pm-bottom-text]'),
        progress: root.querySelector('[data-pm-progress]'),
        progressBar: root.querySelector('[data-pm-progress-bar]'),
        progressFill: root.querySelector('[data-pm-progress-fill]'),
      };
    }

    init() {
      const configEl = this.root.querySelector('[data-pm-config]');
      if (!configEl) return;

      let config;
      try {
        config = JSON.parse(configEl.textContent);
      } catch (err) {
        console.error('[Plow Matcher] Invalid config JSON', err);
        return;
      }

      const list = (key) => (Array.isArray(config[key]) ? config[key] : []);
      this.data = {
        machineTypes: list('machineTypes'),
        machineSizes: list('machineSizes'),
        plowStyles: list('plowStyles'),
        matchers: list('matchers'),
      };
      this.settings = config.settings || {};

      this.bindEvents();

      const restored = this.restoreState();
      if (restored) {
        this.restoring = true;
        this.renderTypes();
        if (this.state.machineType) this.renderSizes();
        if (this.state.machineSize) this.renderStyles();
        if (this.state.plowStyle) {
          this.resolveMatcher();
          this.filterProducts();
          this.renderBottom();
        } else {
          this.filterProducts();
          this.renderBottom();
        }
        this.showStep(restored.step, { instant: true, focus: false });
        this.restoring = false;
        this.announce(this.settings.restoredAnnounce || 'Resumed your previous plow matcher selections.');
      } else {
        this.renderTypes();
        this.filterProducts();
        this.renderBottom();
        this.showStep(1, { instant: true, focus: false });
      }
    }

    /** Persist step + selections so a page refresh resumes mid-flow. */
    saveState() {
      // Fresh entry state — no need to keep storage
      if ((this.step === 1 || this.step === '1') && !this.state.machineType) {
        this.clearSavedState();
        return;
      }

      try {
        const payload = {
          machineType: this.state.machineType,
          machineSize: this.state.machineSize,
          plowStyle: this.state.plowStyle,
          step: this.step,
          updatedAt: Date.now(),
        };
        sessionStorage.setItem(this.storageKey, JSON.stringify(payload));
      } catch (err) {
        // Private mode / quota — fail silently
      }
    }

    clearSavedState() {
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch (err) {
        // ignore
      }
    }

    /**
     * Load and validate saved state against current metaobject data.
     * @returns {{ step: number|string }|null}
     */
    restoreState() {
      let raw;
      try {
        raw = sessionStorage.getItem(this.storageKey);
      } catch (err) {
        return null;
      }
      if (!raw) return null;

      let saved;
      try {
        saved = JSON.parse(raw);
      } catch (err) {
        this.clearSavedState();
        return null;
      }
      if (!saved || typeof saved !== 'object') {
        this.clearSavedState();
        return null;
      }

      let machineType = saved.machineType || null;
      let machineSize = saved.machineSize || null;
      let plowStyle = saved.plowStyle || null;
      let step = saved.step;

      // Validate type
      if (machineType && !this.data.machineTypes.some((t) => idEq(t.id, machineType))) {
        machineType = null;
        machineSize = null;
        plowStyle = null;
      }

      // Validate size belongs to selected type
      if (machineSize) {
        const size = this.data.machineSizes.find((s) => idEq(s.id, machineSize));
        if (!size || !idEq(size.machineTypeId, machineType)) {
          machineSize = null;
          plowStyle = null;
        }
      }

      // Validate plow style
      if (plowStyle && !this.data.plowStyles.some((s) => idEq(s.id, plowStyle))) {
        plowStyle = null;
      }

      // Normalize step from selection depth
      if (!machineType) {
        step = 1;
      } else if (!machineSize) {
        step = step === 1 ? 1 : 2;
      } else if (!plowStyle) {
        if (step === 'result' || step == null) step = 3;
        if (step === 1) step = 2;
      } else if (step !== 3 && step !== 'result') {
        step = 'result';
      }

      // Nothing meaningful to restore beyond defaults
      if ((step === 1 || step === '1') && !machineType) {
        this.clearSavedState();
        return null;
      }

      this.state.machineType = machineType;
      this.state.machineSize = machineSize;
      this.state.plowStyle = plowStyle;
      this.state.matcher = null;

      return { step };
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const back = event.target.closest('[data-pm-back]');
        if (back) {
          event.preventDefault();
          this.goBack();
          return;
        }

        const startOver = event.target.closest('[data-pm-start-over]');
        if (startOver) {
          event.preventDefault();
          this.startOver();
          return;
        }

        const typeBtn = event.target.closest('[data-pm-select-type]');
        if (typeBtn) {
          this.selectType(typeBtn.getAttribute('data-pm-select-type'));
          return;
        }

        const sizeBtn = event.target.closest('[data-pm-select-size]');
        if (sizeBtn) {
          this.selectSize(sizeBtn.getAttribute('data-pm-select-size'));
          return;
        }

        const styleBtn = event.target.closest('[data-pm-select-style]');
        if (styleBtn) {
          this.selectStyle(styleBtn.getAttribute('data-pm-select-style'));
        }
      });

      this.root.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    onKeydown(event) {
      const key = event.key;

      if (key === 'Escape') {
        if (this.step === 1) return;
        event.preventDefault();
        if (this.step === 'result') this.startOver();
        else this.goBack();
        return;
      }

      const option = event.target.closest(OPTION_SEL);
      if (!option || !this.root.contains(option)) return;

      // Enter/Space: native button activation (fires click). Every option is a Tab stop.
      if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) {
        return;
      }

      const grid = option.closest('[data-pm-grid]');
      if (!grid) return;

      const options = Array.from(grid.querySelectorAll(OPTION_SEL)).filter((el) => !el.disabled);
      if (options.length < 2) return;

      const index = options.indexOf(option);
      if (index < 0) return;

      let nextIndex = index;
      if (key === 'ArrowRight' || key === 'ArrowDown') nextIndex = (index + 1) % options.length;
      if (key === 'ArrowLeft' || key === 'ArrowUp') nextIndex = (index - 1 + options.length) % options.length;
      if (key === 'Home') nextIndex = 0;
      if (key === 'End') nextIndex = options.length - 1;

      if (nextIndex === index) return;
      event.preventDefault();
      options[nextIndex].focus();
    }

    selectType(id) {
      if (this.$.progress) this.$.progress.classList.remove('is-faded');
      this.state.machineType = id;
      this.clearFrom('size');
      this.renderSizes();
      this.showStep(2, { focus: true });
      this.saveState();
    }

    selectSize(id) {
      this.state.machineSize = id;
      this.clearFrom('style');
      this.renderStyles();
      this.showStep(3, { focus: true });
      this.saveState();
    }

    selectStyle(id) {
      this.state.plowStyle = id;
      this.resolveMatcher();
      this.filterProducts();
      this.renderBottom();
      this.showStep('result', { focus: true });
      this.saveState();
    }

    clearFrom(level) {
      if (level === 'type') this.state.machineType = null;
      if (level === 'type' || level === 'size') this.state.machineSize = null;
      if (level === 'type' || level === 'size' || level === 'style') this.state.plowStyle = null;
      this.state.matcher = null;
    }

    announce(message) {
      if (this.$.status) this.$.status.textContent = message;
    }

    machineType() {
      return this.data.machineTypes.find((t) => idEq(t.id, this.state.machineType)) || null;
    }

    emptyHtml(message) {
      return `<p class="col-12 text-center text-gray-700 py-5 mb-0 fw-semibold" role="status">${esc(message)}</p>`;
    }

    mediaHtml(src, alt, square) {
      if (src) {
        return `<img src="${esc(src)}" alt="${esc(alt || '')}" width="${square ? 600 : 400}" height="${square ? 600 : 300}" loading="lazy" decoding="async" class="w-100">`;
      }
      return `<div class="pm-card__media--placeholder w-100${square ? ' h-100' : ''}" aria-hidden="true"></div>`;
    }

    cardClass(variant, selected) {
      const selectedCls = selected ? ' is-selected border-primary' : '';
      if (variant === 'type') {
        return `pm-card pm-card--type card border border-gray-100 w-100 h-100 text-start bg-white d-flex flex-column p-4 gap-3 overflow-hidden${selectedCls}`;
      }
      const base =
        'pm-card pm-card--type card border border-gray-100 w-100 h-100 text-start bg-white d-flex flex-column p-4 gap-3 overflow-hidden';
      if (variant === 'size') return `${base} pm-card--size p-4 p-md-4${selectedCls}`;
      if (variant === 'style') return `${base} pm-card--style${selectedCls}`;
      return `${base}${selectedCls}`;
    }

    renderTypes() {
      const grid = this.$.grids.types;
      if (!grid) return;

      const types = this.data.machineTypes.slice().sort(bySortOrder);
      if (!types.length) {
        grid.innerHTML = this.emptyHtml(this.settings.emptyTypes || 'No machine types available.');
        return;
      }

      grid.innerHTML = types
        .map((type) => {
          const selected = idEq(this.state.machineType, type.id);
          return `
            <div class="col-12 col-md-6 col-xl-3 d-flex" role="none">
              <button type="button" class="${this.cardClass('type', selected)}"
                data-pm-select-type="${esc(type.id)}"
                aria-pressed="${selected ? 'true' : 'false'}"
                aria-label="${esc(type.name || 'Machine type')}">
                <span class="pm-card__media d-block w-100" aria-hidden="true">${this.mediaHtml(type.image, type.name, true)}</span>
                <span class="card-body d-flex flex-column gap-1 p-0">
                  <span class="h6 mb-0 text-black">${esc(type.name || '')}</span>
                  ${type.text ? `<span class="text">${esc(type.text)}</span>` : ''}
                </span>
              </button>
            </div>`;
        })
        .join('');
    }

    renderSizes() {
      const grid = this.$.grids.sizes;
      if (!grid) return;

      const sizes = this.data.machineSizes
        .filter((size) => idEq(size.machineTypeId, this.state.machineType))
        .sort(bySortOrder);

      if (this.$.heading2) {
        const question = sizes.map((s) => (s.machineSizeQuestion || '').trim()).find(Boolean);
        const typeName = (this.machineType() || {}).name || '';
        const fallback = (this.settings.step2Heading ||
          'What size [machine_type] are you planning to utilize the attachment on?')
          .replace(/\[machine_type\]/gi, typeName)
          .replace(/\s+/g, ' ')
          .trim();
        this.$.heading2.textContent = question || fallback;
      }

      if (!sizes.length) {
        grid.innerHTML = this.emptyHtml(
          this.settings.emptySizes || 'No machine sizes found for this equipment.'
        );
        return;
      }

      grid.innerHTML = sizes
        .map((size) => {
          const selected = idEq(this.state.machineSize, size.id);
          let examples = size.popularExamples || '';
          if (examples && !examples.toLowerCase().startsWith('popular examples')) {
            examples = `Popular Examples: ${examples}`;
          }
          const label = examples
            ? `${size.title || 'Machine size'}. ${examples}`
            : size.title || 'Machine size';
          return `
            <div class="col-12 col-md-6 col-xl-3 d-flex" role="none">
              <button type="button" class="${this.cardClass('size', selected)}"
                data-pm-select-size="${esc(size.id)}"
                aria-pressed="${selected ? 'true' : 'false'}"
                aria-label="${esc(label)}">
                ${size.image ? `<span class="pm-card__media d-block w-100" aria-hidden="true">${this.mediaHtml(size.image, size.title, true)}</span>` : ''}
                <span class="card-body d-flex flex-column gap-1 p-0">
                  <span class="h6 mb-0 text-black">${esc(size.title || '')}</span>
                  ${examples ? `<span class="text">${esc(examples)}</span>` : ''}
                </span>
              </button>
            </div>`;
        })
        .join('');
    }

    renderStyles() {
      const grid = this.$.grids.styles;
      if (!grid) return;

      const styles = this.data.plowStyles.slice().sort(bySortOrder);
      if (!styles.length) {
        grid.innerHTML = this.emptyHtml(this.settings.emptyStyles || 'No plow styles available.');
        return;
      }

      grid.innerHTML = styles
        .map((style) => {
          const selected = idEq(this.state.plowStyle, style.id);
          return `
            <div class="col-12 col-md-6 col-xl-3 d-flex" role="none">
              <button type="button" class="${this.cardClass('style', selected)}"
                data-pm-select-style="${esc(style.id)}"
                aria-pressed="${selected ? 'true' : 'false'}"
                aria-label="${esc(style.title || 'Plow style')}">
                <span class="pm-card__media d-block w-100" aria-hidden="true">${this.mediaHtml(style.image, style.title, true)}</span>
                <span class="card-body d-flex flex-column gap-1 p-0">
                  <span class="h6 mb-0 text-black">${esc(style.title || '')}</span>
                </span>
              </button>
            </div>`;
        })
        .join('');
    }

    resolveMatcher() {
      const match =
        this.data.matchers.find(
          (m) =>
            idEq(m.machineTypeId, this.state.machineType) &&
            idEq(m.machineSizeId, this.state.machineSize) &&
            idEq(m.plowStyleId, this.state.plowStyle)
        ) || null;
      this.state.matcher = match;
      return match;
    }

    filterProducts() {
      const matcherId = this.state.matcher && this.state.matcher.id;
      let matchCount = 0;

      this.$.products.forEach((item) => {
        const ids = (item.getAttribute('data-matcher-ids') || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
        const isMatch = matcherId != null && ids.some((id) => idEq(id, matcherId));
        item.hidden = !isMatch;
        item.classList.toggle('d-none', !isMatch);
        item.classList.toggle('is-match', isMatch);
        if ('inert' in item) item.inert = !isMatch;
        if (isMatch) matchCount += 1;
      });

      const showEmpty = Boolean(matcherId) && matchCount === 0;
      setVisible(this.$.productsEmpty, showEmpty);

      if (matcherId && matchCount === 0) {
        this.announce(this.settings.emptyProducts || 'No matching plows found.');
      } else if (matchCount > 0) {
        this.announce(`${matchCount} matching plow${matchCount === 1 ? '' : 's'} found.`);
      }
    }

    renderBottom() {
      const wrap = this.$.bottom;
      if (!wrap) return;

      const match = this.state.matcher;
      const heading = match && match.bottomSectionHeading ? String(match.bottomSectionHeading).trim() : '';
      const text = match && match.bottomSectionText ? String(match.bottomSectionText).trim() : '';

      if (!heading && !text) {
        setVisible(wrap, false);
        if (this.$.bottomHeading) this.$.bottomHeading.textContent = '';
        if (this.$.bottomText) this.$.bottomText.innerHTML = '';
        return;
      }

      setVisible(wrap, true);

      if (this.$.bottomHeading) {
        this.$.bottomHeading.textContent = heading;
        setVisible(this.$.bottomHeading, Boolean(heading));
      }
      if (this.$.bottomText) {
        this.$.bottomText.innerHTML = text;
        setVisible(this.$.bottomText, Boolean(text));
      }
    }

    stepEl(step) {
      return this.$.steps[step === 4 ? 'result' : step] || null;
    }

    /**
     * Move focus into a step only after user navigation (never on initial page load).
     * Uses programmatic focus so keyboard users aren't stranded after selecting an option;
     * :focus-visible keeps the ring for keyboard, not mouse.
     */
    focusStep(stepEl, step) {
      if (!stepEl) return;

      const grid = stepEl.querySelector('[data-pm-grid]');
      if (grid) {
        const selected = grid.querySelector(`${OPTION_SEL}[aria-pressed="true"]`);
        const first = grid.querySelector(OPTION_SEL);
        const target = selected || first;
        if (target) {
          target.focus({ preventScroll: true });
          return;
        }
      }

      if (step === 'result') {
        const startOver = stepEl.querySelector('[data-pm-start-over]');
        if (startOver) {
          startOver.focus({ preventScroll: true });
          return;
        }
        const productLink = stepEl.querySelector(
          '[data-pm-product].is-match a[href], [data-pm-product]:not([hidden]) a[href]'
        );
        if (productLink) {
          productLink.focus({ preventScroll: true });
          return;
        }
      }

      const control = stepEl.querySelector('[data-pm-back], [data-pm-start-over]');
      if (control) {
        control.focus({ preventScroll: true });
        return;
      }

      const heading = stepEl.querySelector('[data-pm-heading], .h3');
      if (heading) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    }

    /**
     * Progress bar: fill starts from step 2 (4 screens total).
     * Step 2 = 50%, step 3 = 75%, result = 100%.
     */
    updateProgress(step) {
      const { progress, progressBar, progressFill } = this.$;
      if (!progress || !progressFill) return;

      const isFirst = step === 1 || step === '1';
      if (isFirst) {
        progress.setAttribute('aria-hidden', 'true');
        progressFill.style.width = '0%';
        if (progressBar) progressBar.setAttribute('aria-valuenow', '1');
        return;
      }

      const stepIndex = step === 'result' ? 4 : Number(step);
      const percent = (stepIndex / 4) * 100;

      progress.setAttribute('aria-hidden', 'false');
      progressFill.style.width = `${percent}%`;
      if (progressBar) progressBar.setAttribute('aria-valuenow', String(stepIndex));
    }

    /**
     * @param {number|string} step
     * @param {boolean|{ instant?: boolean, focus?: boolean }} [options]
     *   legacy: true => instant, no focus
     *   focus defaults false on instant; true for animated user-driven moves
     */
    showStep(step, options = {}) {
      if (options === true) options = { instant: true, focus: false };
      else if (typeof options !== 'object' || options == null) options = {};

      const instant = Boolean(options.instant);
      const shouldFocus = options.focus === true;

      if (this.transitioning && !instant) return;

      const next = this.stepEl(step);
      if (!next) return;

      const prev = this.stepEl(this.step);
      const skipMotion = instant || REDUCED_MOTION;

      const activate = () => {
        Object.values(this.$.steps).forEach((el) => {
          if (!el || el === next) return;
          setStepInactive(el);
        });

        setStepActive(next);
        if (!skipMotion) {
          next.classList.add('is-entering');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => next.classList.remove('is-entering'));
          });
        }
        this.step = step === 'result' ? 'result' : step;
        this.transitioning = false;
        this.updateProgress(this.step);

        if (shouldFocus) {
          this.focusStep(next, this.step);
        }

        if (!this.restoring) {
          this.saveState();
        }

        if (!skipMotion && !this.restoring) {
          const top = this.root.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: Math.max(0, top), behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
        }

        if (!this.restoring && !instant) {
          const s = this.settings;
          if (step === 1) this.announce(s.heading || 'Select your equipment');
          if (step === 2) this.announce((this.$.heading2 && this.$.heading2.textContent) || 'Select machine size');
          if (step === 3) this.announce((this.$.heading3 && this.$.heading3.textContent) || 'Select plow style');
          if (step === 'result') this.announce(s.resultHeading || 'Here are your plow matches');
        }
      };

      if (skipMotion || !prev || prev === next || prev.hidden) {
        activate();
        return;
      }

      this.transitioning = true;
      prev.classList.add('is-leaving');
      prev.classList.remove('is-active');
      prev.setAttribute('aria-hidden', 'true');
      if ('inert' in prev) prev.inert = true;

      window.setTimeout(activate, TRANSITION_MS);
    }

    goBack() {
      if (this.step === 2) {
        this.clearFrom('size');
        this.renderBottom();
        this.renderTypes();
        this.showStep(1, { focus: true });
        this.saveState();
        return;
      }
      if (this.step === 3) {
        this.clearFrom('style');
        this.renderBottom();
        this.renderSizes();
        this.showStep(2, { focus: true });
        this.saveState();
        return;
      }
      if (this.step === 'result') {
        this.state.matcher = null;
        this.renderBottom();
        this.filterProducts();
        this.renderStyles();
        this.showStep(3, { focus: true });
        this.saveState();
      }
    }

    startOver() {
      this.clearFrom('type');
      this.clearSavedState();
      if (this.$.grids.sizes) this.$.grids.sizes.innerHTML = '';
      if (this.$.grids.styles) this.$.grids.styles.innerHTML = '';
      if (this.$.progress) this.$.progress.classList.add('is-faded');
      this.filterProducts();
      this.renderBottom();
      this.renderTypes();
      this.showStep(1, { focus: false });
      this.announce(this.settings.startOverAnnounce || 'Selections cleared. Choose your equipment type.');
    }
  }

  function boot() {
    document.querySelectorAll('[data-plow-matcher]').forEach((root) => {
      if (root.dataset.pmReady === 'true') return;
      new PlowMatcher(root).init();
      root.dataset.pmReady = 'true';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
